import * as React from 'react';
import Button, { ButtonContext, ButtonType, ButtonSize } from './Button';

const ButtonExamples: React.FC = () => {
  const handleClick = () => {
    alert('Button clicked');
  };

  const contexts: ButtonContext[] = ['Candidates', 'Companies'];
  const types: ButtonType[] = ['Primary', 'Secondary', 'Terceary', 'Primary-Icon', 'Secondary-Icon', 'Terceary-Icon'];
  const sizes: ButtonSize[] = ['S', 'M', 'L'];

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1>Button Component Examples</h1>
      
      {/* Links (Anchor Tags) */}
      <section style={{ marginBottom: '40px' }}>
        <h2>As Links (href prop)</h2>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
          <Button href="/profile">Go to Profile</Button>
          <Button href="/jobs" context="Candidates" type="Secondary">
            Browse Jobs
          </Button>
          <Button href="/companies" context="Companies" type="Primary" size="M">
            View Companies
          </Button>
        </div>
        <h3>With Icons</h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
          <Button href="/back" iconLeftName="arrowLeft">
            Go Back
          </Button>
          <Button href="/next" iconRightName="chevronRight" type="Secondary">
            Continue
          </Button>
          <Button href="/profile" iconLeftName="user" iconRightName="chevronRight">
            My Profile
          </Button>
        </div>
        <h3>External Links</h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button href="https://example.com" target="_blank">
            External Site
          </Button>
          <Button href="https://docs.example.com" target="_blank" type="Secondary" iconRightName="externalLink">
            Documentation
          </Button>
        </div>
      </section>
      
      {/* Size variations */}
      <section style={{ marginBottom: '40px' }}>
        <h2>Size Variations</h2>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button size="S" onClick={handleClick}>Small Button</Button>
          <Button size="M" onClick={handleClick}>Medium Button</Button>
          <Button size="L" onClick={handleClick}>Large Button</Button>
        </div>
      </section>

      {/* Context: Candidates */}
      <section style={{ marginBottom: '40px' }}>
        <h2>Candidates Context</h2>
        {sizes.map(size => (
          <div key={size} style={{ marginBottom: '24px' }}>
            <h3>Size {size}</h3>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button context="Candidates" type="Primary" size={size} onClick={handleClick}>
                Primary
              </Button>
              <Button context="Candidates" type="Secondary" size={size} onClick={handleClick}>
                Secondary
              </Button>
              <Button context="Candidates" type="Terceary" size={size} onClick={handleClick}>
                Terceary
              </Button>
              <Button
                context="Candidates"
                type="Primary-Icon"
                size={size}
                onClick={handleClick}
                iconLeftName="chevronLeft"
                iconRightName="chevronRight"
              >
                With Icons
              </Button>
              <Button
                context="Candidates"
                type="Secondary-Icon"
                size={size}
                onClick={handleClick}
                iconLeftName="arrowLeft"
              >
                Left Icon
              </Button>
              <Button
                context="Candidates"
                type="Terceary-Icon"
                size={size}
                onClick={handleClick}
                iconRightName="arrowLeft"
              >
                Right Icon
              </Button>
            </div>
          </div>
        ))}
      </section>

      {/* Context: Companies */}
      <section style={{ marginBottom: '40px' }}>
        <h2>Companies Context</h2>
        {sizes.map(size => (
          <div key={size} style={{ marginBottom: '24px' }}>
            <h3>Size {size}</h3>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button context="Companies" type="Primary" size={size} onClick={handleClick}>
                Primary
              </Button>
              <Button context="Companies" type="Secondary" size={size} onClick={handleClick}>
                Secondary
              </Button>
              <Button context="Companies" type="Terceary" size={size} onClick={handleClick}>
                Terceary
              </Button>
              <Button
                context="Companies"
                type="Primary-Icon"
                size={size}
                onClick={handleClick}
                iconLeftName="chevronLeft"
                iconRightName="chevronRight"
              >
                With Icons
              </Button>
              <Button
                context="Companies"
                type="Secondary-Icon"
                size={size}
                onClick={handleClick}
                iconLeftName="arrowLeft"
              >
                Left Icon
              </Button>
              <Button
                context="Companies"
                type="Terceary-Icon"
                size={size}
                onClick={handleClick}
                iconRightName="arrowLeft"
              >
                Right Icon
              </Button>
            </div>
          </div>
        ))}
      </section>

      {/* States */}
      <section style={{ marginBottom: '40px' }}>
        <h2>States</h2>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button onClick={handleClick}>Default</Button>
          <Button disabled onClick={handleClick}>Disabled</Button>
        </div>
      </section>

      {/* Dark Mode */}
      <section style={{ marginBottom: '40px', backgroundColor: '#1a1a1a', padding: '20px', borderRadius: '8px' }}>
        <h2 style={{ color: 'white' }}>Dark Mode</h2>
        <h3 style={{ color: 'white' }}>Candidates Context</h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
          <Button mode="Dark" context="Candidates" type="Primary" onClick={handleClick}>
            Primary
          </Button>
          <Button mode="Dark" context="Candidates" type="Secondary" onClick={handleClick}>
            Secondary
          </Button>
          <Button mode="Dark" context="Candidates" type="Terceary" onClick={handleClick}>
            Terceary
          </Button>
        </div>
        <h3 style={{ color: 'white' }}>Companies Context</h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
          <Button mode="Dark" context="Companies" type="Primary" onClick={handleClick}>
            Primary
          </Button>
          <Button mode="Dark" context="Companies" type="Secondary" onClick={handleClick}>
            Secondary
          </Button>
          <Button mode="Dark" context="Companies" type="Terceary" onClick={handleClick}>
            Terceary
          </Button>
        </div>
        <h3 style={{ color: 'white' }}>As Links</h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button mode="Dark" href="/profile" iconLeftName="user">
            Profile
          </Button>
          <Button mode="Dark" href="/settings" type="Secondary">
            Settings
          </Button>
        </div>
      </section>

      {/* Icon Examples */}
      <section style={{ marginBottom: '40px' }}>
        <h2>Icon Variations</h2>
        <h3>Icon by Name (iconLeftName / iconRightName)</h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
          <Button onClick={handleClick} iconLeftName="arrowLeft">
            Back
          </Button>
          <Button onClick={handleClick} iconRightName="chevronRight">
            Next
          </Button>
          <Button onClick={handleClick} iconLeftName="eye" iconRightName="eyeClosed">
            Toggle
          </Button>
        </div>
        <h3>Custom Icon Components (iconLeft / iconRight)</h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button onClick={handleClick} iconLeft={<span>←</span>}>
            Custom Left
          </Button>
          <Button onClick={handleClick} iconRight={<span>→</span>}>
            Custom Right
          </Button>
        </div>
      </section>

      {/* Usage Examples */}
      <section style={{ marginBottom: '40px' }}>
        <h2>Real-World Usage Examples</h2>
        <h3>Application Flow</h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
          <Button onClick={handleClick}>Apply Now</Button>
          <Button
            context="Candidates"
            type="Secondary"
            onClick={handleClick}
            iconLeftName="arrowLeft"
          >
            Back
          </Button>
          <Button
            context="Candidates"
            type="Primary"
            onClick={handleClick}
            iconRightName="chevronRight"
          >
            Continue
          </Button>
        </div>
        <h3>Company Actions</h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
          <Button 
            context="Companies" 
            type="Primary" 
            size="L" 
            onClick={handleClick}
          >
            Post Job
          </Button>
          <Button 
            context="Companies" 
            type="Secondary" 
            href="/dashboard"
          >
            View Dashboard
          </Button>
        </div>
        <h3>Success Screen</h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button href="/profile" type="Primary" size="M">
            Complete my profile
          </Button>
          <Button href="/jobs" type="Secondary" size="M">
            See other jobs
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ButtonExamples;
